import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { ClipLoader } from 'react-spinners';
import {
  EyeIcon,
  PencilSquareIcon,
  PlusIcon,
  PhotoIcon,
  ArrowUpTrayIcon,
  EnvelopeIcon,
  GlobeAltIcon,
  MapPinIcon,
  CheckBadgeIcon,
  TagIcon,
  CalendarDaysIcon,
  ArrowRightIcon,
  LinkIcon,
  XMarkIcon,
  CheckIcon,
  LockClosedIcon,
  TrashIcon,
  CameraIcon,
} from '@heroicons/react/24/outline';

import { useAlert } from 'context/AlertContext';
import { useImageCropper } from 'hooks/useImageCropper';
import ImageCropper from 'components/ImageCropper';
import CategorySelectorGrid from 'components/CategorySelectorGrid';
import { categories as CATEGORY_TREE, toCategoryId } from 'utils/categories';
import {
  validateFiles,
  combinedUploadError,
  uploadErrorMessage,
  IMAGE_ACCEPT,
} from 'utils/uploadValidation';

import { updateBrandProfile } from '../../../redux/actions/brands';

import { isNameTakenError } from 'utils/nameTaken';
import facebookIcon from '../../../assets/icons/socials/facebook_icon.svg';
import instagramIcon from '../../../assets/icons/socials/instagram_icon.svg';
import tiktokIcon from '../../../assets/icons/socials/tiktok_icon.svg';

const MAX_MEDIA = 10;

const makeId = () => {
  if (typeof window !== 'undefined' && window.crypto?.randomUUID) {
    return window.crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

const SOCIAL_ICONS = {
  facebook: facebookIcon,
  instagram: instagramIcon,
  tiktok: tiktokIcon,
};

const SOCIAL_LABELS = {
  facebook: 'Facebook',
  instagram: 'Instagram',
  tiktok: 'TikTok',
};

const initialsOf = (str) => {
  if (!str || typeof str !== 'string') return '?';
  const parts = str.trim().split(/\s+/).slice(0, 2);
  return parts.map((p) => p.charAt(0).toUpperCase()).join('') || '?';
};

const safeArray = (raw) => {
  if (Array.isArray(raw)) return raw;
  if (typeof raw !== 'string') return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

// eslint-disable-next-line no-unused-vars
const computeCompleteness = (brand) => {
  if (!brand) return 0;
  const checks = [
    !!brand.username,
    !!brand.email,
    !!(brand.city || brand.country),
    !!brand.foundingYear,
    !!(brand.description || brand.longDescription),
    safeArray(brand.keywords).length > 0,
    safeArray(brand.categories).length > 0,
    Array.isArray(brand.media) && brand.media.length > 0,
    !!brand.avatar,
    !!(brand.website || brand.socials?.facebook || brand.socials?.instagram),
  ];
  const filled = checks.filter(Boolean).length;
  return Math.round((filled / checks.length) * 100);
};

const Eyebrow = ({ children }) => (
  <div className="text-[11px] uppercase tracking-[0.14em] text-muted font-bold mb-1.5">
    {children}
  </div>
);

const Card = ({ children, className = '', padded = true }) => (
  <section
    className={`bg-white border border-line rounded-2xl shadow-softer ${
      padded ? 'p-6' : ''
    } ${className}`}
  >
    {children}
  </section>
);

const CardHeader = ({ eyebrow, title, sub, action }) => (
  <header className="flex items-start justify-between gap-4 mb-5">
    <div className="min-w-0">
      {eyebrow && <Eyebrow>{eyebrow}</Eyebrow>}
      <h3 className="text-[17px] sm:text-[19px] font-bold tracking-tight text-ink-strong leading-snug">
        {title}
      </h3>
      {sub && (
        <p className="text-sm text-muted mt-1 max-w-prose">{sub}</p>
      )}
    </div>
    {action}
  </header>
);

// eslint-disable-next-line no-unused-vars
const Progress = ({ value }) => (
  <div className="h-1.5 rounded-full bg-line overflow-hidden">
    <div
      className="h-full bg-brand rounded-full"
      style={{ width: `${Math.max(0, Math.min(100, value))}%` }}
    />
  </div>
);

const PencilBtn = ({ onClick, label }) => (
  <button
    type="button"
    onClick={onClick}
    aria-label={label}
    className="w-9 h-9 rounded-full hover:bg-paper-2 inline-flex items-center justify-center text-ink shrink-0"
  >
    <PencilSquareIcon className="w-[18px] h-[18px]" />
  </button>
);

const EditActions = ({ onCancel, onSave, saving }) => {
  const { t } = useTranslation();
  return (
    <div className="mt-5 pt-4 border-t border-line flex items-center justify-end gap-2">
      <button
        type="button"
        onClick={onCancel}
        disabled={saving}
        className="inline-flex items-center gap-1.5 h-9 px-4 rounded-full text-ink hover:bg-paper-2 text-[13px] font-bold disabled:opacity-50"
      >
        <XMarkIcon className="w-4 h-4" />
        {t('dashboard.cancel')}
      </button>
      <button
        type="button"
        onClick={onSave}
        disabled={saving}
        className="inline-flex items-center gap-1.5 h-9 px-4 rounded-full bg-brand hover:bg-brand-hover text-ink-strong text-[13px] font-bold shadow-softer disabled:opacity-60"
      >
        {saving ? (
          <ClipLoader color="#1A1A1F" size={14} />
        ) : (
          <CheckIcon className="w-4 h-4" />
        )}
        {saving ? t('dashboard.saving') : t('dashboard.save')}
      </button>
    </div>
  );
};

const TextInput = (props) => (
  <input
    type="text"
    {...props}
    className={`w-full h-11 px-3.5 rounded-xl bg-white border border-line text-[14px] text-ink placeholder:text-muted focus:outline-none focus:border-yellow-03 transition-colors ${
      props.className || ''
    }`}
  />
);

const Textarea = (props) => (
  <textarea
    {...props}
    className={`w-full min-h-[120px] px-3.5 py-3 rounded-xl bg-white border border-line text-[14px] text-ink placeholder:text-muted focus:outline-none focus:border-yellow-03 transition-colors resize-y ${
      props.className || ''
    }`}
  />
);

const FieldLabel = ({ children }) => (
  <div className="text-[10.5px] uppercase tracking-[0.08em] text-yellow-03 font-bold mb-1.5">
    {children}
  </div>
);

const ContactView = ({ icon: Icon, label, value, href, locked }) => {
  const empty = !value;
  const body = (
    <div className="flex items-center gap-3">
      <div className="w-9 h-9 rounded-lg bg-brand-softer flex items-center justify-center shrink-0">
        <Icon className="w-4 h-4 text-yellow-03" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-[10.5px] uppercase tracking-[0.08em] text-yellow-03 font-bold flex items-center gap-1.5">
          {label}
          {locked && <LockClosedIcon className="w-3 h-3 text-muted" />}
        </div>
        <div
          className={`text-[13.5px] truncate ${
            empty
              ? 'text-muted italic'
              : href
              ? 'text-ink-strong underline underline-offset-2 decoration-line'
              : 'text-ink'
          }`}
          title={value || ''}
        >
          {value || '—'}
        </div>
      </div>
    </div>
  );

  if (href && value) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="block hover:opacity-90"
      >
        {body}
      </a>
    );
  }
  return body;
};

const GALLERY_TINTS = [
  '#FFE2A8',
  '#FFD7C2',
  '#E8E2D0',
  '#F2DCD0',
  '#EAE0CF',
  '#DCD2C0',
  '#FFEDC9',
  '#E5DBC3',
  '#F0DDC2',
];

const BrandInfo = ({ brand, loading, error, onEdit }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { showAlert } = useAlert();

  const [editingSection, setEditingSection] = useState(null);
  const [saving, setSaving] = useState(false);

  // Per-section local form state
  const [identityForm, setIdentityForm] = useState({ username: '' });
  const [descForm, setDescForm] = useState({
    description: '',
    longDescription: '',
    history: '',
    keywords: [],
    keywordDraft: '',
  });
  const [contactForm, setContactForm] = useState({ website: '' });
  const [socialForm, setSocialForm] = useState({
    facebook: '',
    instagram: '',
    tiktok: '',
    others: [],
  });

  // Media editor state — single ordered list; items[0] is the cover.
  // item: { key, kind: 'existing' | 'new', url?, id?, file?, preview? }
  const [mediaForm, setMediaForm] = useState({ items: [] });
  const previewUrlsRef = useRef(new Set());
  const fileInputRef = useRef(null);

  // Categories editor state — controlled selection passed to CategorySelectorGrid
  const [catsForm, setCatsForm] = useState({ macros: [], micros: [] });

  // Memoized derived data (always before any early return)
  const keywords = useMemo(() => safeArray(brand?.keywords), [brand?.keywords]);
  const categories = useMemo(
    () => safeArray(brand?.categories),
    [brand?.categories]
  );
  const media = useMemo(
    () => (Array.isArray(brand?.media) ? brand.media : []),
    [brand?.media]
  );

  const socialList = useMemo(() => {
    const socials = brand?.socials || {};
    const list = [];
    ['facebook', 'instagram', 'tiktok'].forEach((key) => {
      if (socials?.[key]) {
        list.push({ key, label: SOCIAL_LABELS[key], url: socials[key] });
      }
    });
    (Array.isArray(socials?.others) ? socials.others : []).forEach((o) => {
      if (o?.url) {
        list.push({
          key: (o.label || 'link').toLowerCase(),
          label: o.label || 'Link',
          url: o.url,
        });
      }
    });
    return list;
  }, [brand?.socials]);

  // ---------- handlers ----------
  const closeEditor = useCallback(() => setEditingSection(null), []);

  const startEditIdentity = useCallback(() => {
    setIdentityForm({ username: brand?.username || '' });
    setEditingSection('identity');
  }, [brand?.username]);

  const startEditDescription = useCallback(() => {
    setDescForm({
      description: brand?.description || '',
      longDescription: brand?.longDescription || '',
      history: brand?.history || '',
      keywords: [...keywords],
      keywordDraft: '',
    });
    setEditingSection('description');
  }, [brand?.description, brand?.longDescription, brand?.history, keywords]);

  const startEditContact = useCallback(() => {
    setContactForm({ website: brand?.website || '' });
    setEditingSection('contact');
  }, [brand?.website]);

  const startEditSocial = useCallback(() => {
    const s = brand?.socials || {};
    const others = Array.isArray(s.others)
      ? s.others.map((o) => ({
          id: makeId(),
          label: o?.label || '',
          url: o?.url || '',
        }))
      : [];
    setSocialForm({
      facebook: s.facebook || '',
      instagram: s.instagram || '',
      tiktok: s.tiktok || '',
      others,
    });
    setEditingSection('social');
  }, [brand?.socials]);

  const addCustomSocial = useCallback(() => {
    setSocialForm((prev) => ({
      ...prev,
      others: [...prev.others, { id: makeId(), label: '', url: '' }],
    }));
  }, []);
  const removeCustomSocial = useCallback((id) => {
    setSocialForm((prev) => ({
      ...prev,
      others: prev.others.filter((o) => o.id !== id),
    }));
  }, []);
  const updateCustomSocial = useCallback((id, key, value) => {
    setSocialForm((prev) => ({
      ...prev,
      others: prev.others.map((o) => (o.id === id ? { ...o, [key]: value } : o)),
    }));
  }, []);

  // ---------- media (gallery) ----------
  const revokePreviewUrls = useCallback(() => {
    previewUrlsRef.current.forEach((url) => {
      try {
        URL.revokeObjectURL(url);
      } catch {
        /* ignore */
      }
    });
    previewUrlsRef.current.clear();
  }, []);

  const startEditMedia = useCallback(() => {
    revokePreviewUrls();
    setMediaForm({
      items: (Array.isArray(brand?.media) ? brand.media : []).map((url) => ({
        key: `existing:${url}`,
        kind: 'existing',
        url,
      })),
    });
    setEditingSection('media');
  }, [brand?.media, revokePreviewUrls]);

  const cancelMedia = useCallback(() => {
    revokePreviewUrls();
    setMediaForm({ items: [] });
    setEditingSection(null);
  }, [revokePreviewUrls]);

  const showUploadErrors = useCallback(
    (uploadErrors) => {
      showAlert({
        type: 'warning',
        title: t('upload.error_title', { defaultValue: 'Upload issue' }),
        message: uploadErrors.join('\n'),
        primaryAction: { label: t('common.ok') },
      });
    },
    [showAlert, t]
  );

  const handleMediaPick = useCallback(
    (e) => {
      const input = e.target;
      const picked = Array.from(input.files || []);
      if (!picked.length) return;
      const existingBytes = mediaForm.items.reduce(
        (sum, it) => sum + (it.file?.size || 0),
        0
      );
      const { accepted: files, errors: uploadErrors } = validateFiles(picked, {
        kind: 'image',
        existingBytes,
        t,
      });
      if (uploadErrors.length) showUploadErrors(uploadErrors);
      if (!files.length) {
        input.value = '';
        return;
      }
      setMediaForm((prev) => {
        const remaining = MAX_MEDIA - prev.items.length;
        const allowed = files.slice(0, Math.max(0, remaining));
        const added = allowed.map((file) => {
          const id = makeId();
          const preview = URL.createObjectURL(file);
          previewUrlsRef.current.add(preview);
          return { key: `new:${id}`, kind: 'new', id, file, preview };
        });
        return { items: [...prev.items, ...added] };
      });
      input.value = '';
    },
    [mediaForm.items, showUploadErrors, t]
  );

  const removeMedia = useCallback((key) => {
    setMediaForm((prev) => {
      const item = prev.items.find((x) => x.key === key);
      if (item?.kind === 'new' && item.preview) {
        try {
          URL.revokeObjectURL(item.preview);
        } catch {
          /* ignore */
        }
        previewUrlsRef.current.delete(item.preview);
      }
      return { items: prev.items.filter((x) => x.key !== key) };
    });
  }, []);

  // Promote an image to cover by moving it to the front of the list.
  const setAsCover = useCallback((key) => {
    setMediaForm((prev) => {
      const idx = prev.items.findIndex((x) => x.key === key);
      if (idx <= 0) return prev; // already cover or not found
      const next = [...prev.items];
      const [item] = next.splice(idx, 1);
      next.unshift(item);
      return { items: next };
    });
  }, []);

  // ---------- categories ----------
  const startEditCategories = useCallback(() => {
    const initial = safeArray(brand?.categories);
    // toCategoryId: legacy brands store ids as strings, and both the picker and
    // the taxonomy lookup in saveCategories match with ===.
    setCatsForm({
      macros: [...new Set(initial.map((c) => toCategoryId(c.macroCategory)))],
      micros: [
        ...new Set(
          initial
            .flatMap((c) =>
              Array.isArray(c.microCategory) ? c.microCategory : []
            )
            .map(toCategoryId)
        ),
      ],
    });
    setEditingSection('categories');
  }, [brand?.categories]);

  const setCatsMacros = useCallback((next) => {
    setCatsForm((prev) => ({
      ...prev,
      macros: typeof next === 'function' ? next(prev.macros) : next,
    }));
  }, []);
  const setCatsMicros = useCallback((next) => {
    setCatsForm((prev) => ({
      ...prev,
      micros: typeof next === 'function' ? next(prev.micros) : next,
    }));
  }, []);

  const showSaveError = useCallback(
    (err) => {
      const uploadMessage = uploadErrorMessage(err, t);
      showAlert({
        title: t('dashboard.edit.save_error_title'),
        message: isNameTakenError(err)
          ? t('dashboard.name_taken')
          : uploadMessage || t('dashboard.edit.save_error_message'),
        primaryAction: { label: t('dashboard.edit.save_error_action') },
      });
    },
    [showAlert, t]
  );

  const submitFormData = useCallback(
    async (entries) => {
      if (!brand?.id) return;
      setSaving(true);
      try {
        const fd = new FormData();
        Object.entries(entries).forEach(([k, v]) => {
          if (v === undefined) return;
          fd.append(k, v === null ? '' : v);
        });
        await dispatch(updateBrandProfile(brand.id, fd));
        setEditingSection(null);
      } catch (e) {
        showSaveError(e);
      } finally {
        setSaving(false);
      }
    },
    [brand?.id, dispatch, showSaveError]
  );

  const saveIdentity = useCallback(() => {
    const name = identityForm.username.trim();
    if (!name) {
      showAlert({
        type: 'warning',
        title: t('dashboard.edit.save_error_title'),
        message: t('dashboard.name_required', 'Name cannot be empty'),
        primaryAction: { label: t('common.ok') },
      });
      return;
    }
    submitFormData({ username: name });
  }, [identityForm, submitFormData, showAlert, t]);

  const saveDescription = useCallback(() => {
    submitFormData({
      description: descForm.description.trim(),
      longDescription: descForm.longDescription.trim(),
      keywords: JSON.stringify(
        descForm.keywords.map((k) => String(k).trim()).filter(Boolean)
      ),
      // history is not in the API destructure list — skip until backend exposes it
    });
  }, [descForm, submitFormData]);

  const saveContact = useCallback(() => {
    submitFormData({ website: contactForm.website.trim() });
  }, [contactForm, submitFormData]);

  const saveSocial = useCallback(() => {
    const cleanedOthers = socialForm.others
      .map((o) => ({ label: o.label.trim(), url: o.url.trim() }))
      .filter((o) => o.label && o.url);
    const payload = {
      website: brand?.website || '',
      facebook: socialForm.facebook.trim(),
      instagram: socialForm.instagram.trim(),
      tiktok: socialForm.tiktok.trim(),
      others: cleanedOthers,
    };
    submitFormData({ socials: JSON.stringify(payload) });
  }, [socialForm, brand?.website, submitFormData]);

  const saveMedia = useCallback(async () => {
    if (!brand?.id) return;

    const combinedError = combinedUploadError(
      mediaForm.items.map((it) => it.file),
      t
    );
    if (combinedError) {
      showUploadErrors([combinedError]);
      return;
    }

    setSaving(true);
    try {
      const fd = new FormData();
      const { items } = mediaForm;

      const existingUrls = items
        .filter((it) => it.kind === 'existing')
        .map((it) => it.url);
      const original = Array.isArray(brand?.media) ? brand.media : [];
      const removed = original.filter((u) => !existingUrls.includes(u));
      fd.append('keepMedia', JSON.stringify(existingUrls));
      fd.append('removedMedia', JSON.stringify(removed));

      // Full desired order (items[0] is the cover). Tokens are kept URLs or
      // "new:<idx>" pointing at the Nth appended file, so a newly uploaded
      // image can become the cover in the same save.
      let newIndex = 0;
      const mediaOrder = items.map((it) => {
        if (it.kind === 'existing') return it.url;
        const token = `new:${newIndex}`;
        newIndex += 1;
        return token;
      });
      fd.append('mediaOrder', JSON.stringify(mediaOrder));

      items
        .filter((it) => it.kind === 'new')
        .forEach(({ file }) => fd.append('media', file));

      await dispatch(updateBrandProfile(brand.id, fd));
      revokePreviewUrls();
      setMediaForm({ items: [] });
      setEditingSection(null);
    } catch (e) {
      showSaveError(e);
    } finally {
      setSaving(false);
    }
  }, [
    brand?.id,
    brand?.media,
    mediaForm,
    dispatch,
    revokePreviewUrls,
    showSaveError,
    showUploadErrors,
    t,
  ]);

  const saveCategories = useCallback(() => {
    const payload = catsForm.macros.map((m) => ({
      macroCategory: m,
      microCategory: catsForm.micros.filter((mi) =>
        (CATEGORY_TREE.find((c) => c.id === m)?.micro || []).some(
          (x) => x.id === mi
        )
      ),
    }));
    submitFormData({ categories: JSON.stringify(payload) });
  }, [catsForm, submitFormData]);

  // ---- avatar (crop + immediate save) ----
  const {
    imagePreview,
    croppedImagePreview,
    isCropping,
    handleImageChange,
    handleCropSave,
    setIsCropping,
    resetImage,
  } = useImageCropper();
  const [savingAvatar, setSavingAvatar] = useState(false);

  const onAvatarPick = (e) => {
    const files = Array.from(e.target.files || []);
    const { accepted, errors: uploadErrors } = validateFiles(files, {
      kind: 'image',
      t,
    });
    if (uploadErrors.length) {
      showUploadErrors(uploadErrors);
      return;
    }
    if (accepted.length) handleImageChange(e);
  };

  const submitAvatar = async (blob) => {
    if (!brand?.id) return;
    setSavingAvatar(true);
    try {
      const fd = new FormData();
      fd.append('avatar', blob, 'avatar.jpg');
      await dispatch(updateBrandProfile(brand.id, fd));
    } catch (err) {
      resetImage();
      showSaveError(err);
    } finally {
      setSavingAvatar(false);
    }
  };

  // Reset editor when the brand changes underneath us
  useEffect(() => {
    setEditingSection(null);
  }, [brand?.id]);

  // Revoke any leftover blob URLs on unmount
  useEffect(() => {
    return () => {
      revokePreviewUrls();
    };
  }, [revokePreviewUrls]);

  if (loading && !brand) {
    return (
      <div className="flex items-center justify-center py-16">
        <ClipLoader color="#FFBD59" size={40} />
      </div>
    );
  }

  if (!brand) {
    return (
      <div className="py-16 text-center text-muted text-sm">
        {error ? t('dashboard.failed_to_load') : t('dashboard.no_brand_info')}
      </div>
    );
  }

  const locationStr = [brand.city, brand.country].filter(Boolean).join(', ');
  const websiteHref =
    brand.website && !/^https?:\/\//i.test(brand.website)
      ? `https://${brand.website}`
      : brand.website || null;

  const isEditing = (section) => editingSection === section;
  const canStartEditing = editingSection === null;

  // Keyword editor handlers (used inside the description editor)
  const addKeyword = () => {
    const k = descForm.keywordDraft.trim();
    if (!k) return;
    setDescForm((prev) => ({
      ...prev,
      keywords: prev.keywords.includes(k) ? prev.keywords : [...prev.keywords, k],
      keywordDraft: '',
    }));
  };
  const removeKeyword = (idx) =>
    setDescForm((prev) => ({
      ...prev,
      keywords: prev.keywords.filter((_, i) => i !== idx),
    }));

  return (
    <div className="space-y-6">
      {/* Identity card */}
      <Card padded={false} className="overflow-hidden">
        <div className="relative">
          <div className="h-24 sm:h-28 bg-gradient-to-br from-brand-soft via-brand-softer to-paper" />
          <div className="px-5 sm:px-7 pb-6 -mt-12 flex flex-col sm:flex-row sm:items-end gap-4 sm:gap-5 flex-wrap">
            <div className="relative shrink-0 mx-auto sm:mx-0">
              <div className="w-[88px] h-[88px] rounded-full bg-white p-1 shadow-card">
                {croppedImagePreview || brand.avatar ? (
                  <img
                    src={croppedImagePreview || brand.avatar}
                    alt=""
                    className="w-full h-full rounded-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full rounded-full bg-brand text-white flex items-center justify-center font-bold text-[28px]">
                    {initialsOf(brand.username)}
                  </div>
                )}
              </div>
              <input
                id="brand-avatar-input"
                type="file"
                accept={IMAGE_ACCEPT}
                className="sr-only"
                disabled={savingAvatar}
                onChange={(e) => {
                  onAvatarPick(e);
                  e.target.value = '';
                }}
              />
              <label
                htmlFor="brand-avatar-input"
                className="absolute bottom-0 right-0 inline-flex items-center justify-center w-8 h-8 rounded-full bg-white border border-line shadow-softer cursor-pointer hover:bg-paper-2"
                title={t('dashboard.change_avatar', 'Change photo')}
              >
                {savingAvatar ? (
                  <ClipLoader color="#9CA3AF" size={14} />
                ) : (
                  <CameraIcon className="w-4 h-4 text-ink" />
                )}
              </label>
            </div>
            <div className="flex-1 min-w-0 text-center sm:text-left">
              {isEditing('identity') ? (
                <div className="max-w-md mx-auto sm:mx-0 text-left">
                  <FieldLabel>
                    {t('dashboard.brand_name', 'Brand name')}
                  </FieldLabel>
                  <TextInput
                    value={identityForm.username}
                    maxLength={100}
                    onChange={(e) =>
                      setIdentityForm({ username: e.target.value })
                    }
                  />
                  <EditActions
                    onCancel={closeEditor}
                    onSave={saveIdentity}
                    saving={saving}
                  />
                </div>
              ) : (
                <div className="flex items-center justify-center sm:justify-start gap-2 flex-wrap">
                  <h2
                    className="text-[22px] sm:text-[26px] font-bold tracking-tight text-ink-strong leading-tight break-words"
                    title={brand.username || ''}
                  >
                    {brand.username || '—'}
                  </h2>
                  {brand.emailConfirmed && (
                    <span className="inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.08em] font-bold text-emerald-700 bg-emerald-50 rounded-full px-2.5 py-1">
                      <CheckBadgeIcon className="w-3.5 h-3.5" />
                      {t('dashboard.verified')}
                    </span>
                  )}
                  {canStartEditing && (
                    <PencilBtn
                      onClick={startEditIdentity}
                      label={t('dashboard.edit_profile')}
                    />
                  )}
                </div>
              )}
              <div className="flex items-center justify-center sm:justify-start gap-4 mt-2 text-[12.5px] text-muted flex-wrap">
                <span className="inline-flex items-center gap-1.5">
                  <TagIcon className="w-3.5 h-3.5" />
                  {t('dashboard.info.brand_type')}
                </span>
                {locationStr && (
                  <span className="inline-flex items-center gap-1.5">
                    <MapPinIcon className="w-3.5 h-3.5" />
                    {locationStr}
                  </span>
                )}
                {brand.foundingYear && (
                  <span className="inline-flex items-center gap-1.5">
                    <CalendarDaysIcon className="w-3.5 h-3.5" />
                    {t('dashboard.info.since', { year: brand.foundingYear })}
                  </span>
                )}
              </div>
            </div>
            {brand.slug && (
              <button
                type="button"
                onClick={() => navigate(`/brands/${brand.slug}`)}
                className="inline-flex items-center justify-center gap-2 h-10 px-4 rounded-full bg-white text-ink-strong border border-line hover:border-ink/30 text-sm font-bold transition-colors shrink-0 mx-auto sm:mx-0"
              >
                <EyeIcon className="w-4 h-4" />
                {t('dashboard.info.view_public')}
              </button>
            )}
            {/* Profile completeness — hidden for now, will return once we
                decide what the % should reflect.
            <div className="w-full sm:w-[220px] mb-1">
              ...
            </div>
            */}
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main column */}
        <div className="lg:col-span-2 space-y-6 min-w-0">
          {/* Gallery — inline edit */}
          <Card>
            <CardHeader
              eyebrow={t('dashboard.info.gallery_eyebrow')}
              title={t('dashboard.info.gallery_title')}
              sub={t('dashboard.info.gallery_sub')}
              action={
                isEditing('media') ? (
                  <span className="text-[12px] text-muted font-bold mono">
                    {t('dashboard.edit.media_count', {
                      used: mediaForm.items.length,
                      max: MAX_MEDIA,
                    })}
                  </span>
                ) : (
                  canStartEditing && (
                    <button
                      type="button"
                      onClick={startEditMedia}
                      className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full bg-white text-ink-strong border border-line hover:border-ink/30 text-[13px] font-bold"
                    >
                      <ArrowUpTrayIcon className="w-4 h-4" />
                      {t('dashboard.info.upload')}
                    </button>
                  )
                )
              }
            />
            {isEditing('media') ? (
              <>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept={IMAGE_ACCEPT}
                  multiple
                  className="sr-only"
                  onChange={handleMediaPick}
                />
                {mediaForm.items.length > 0 ? (
                  <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-5 gap-3">
                    {mediaForm.items.map((item, i) => {
                      const isCover = i === 0;
                      const src =
                        item.kind === 'existing' ? item.url : item.preview;
                      return (
                        <div
                          key={item.key}
                          className={`relative aspect-square rounded-xl overflow-hidden bg-paper-2 group ${
                            isCover ? 'ring-2 ring-yellow-03' : ''
                          }`}
                        >
                          <img
                            src={src}
                            alt=""
                            className="absolute inset-0 w-full h-full object-cover"
                            loading="lazy"
                          />
                          {isCover && (
                            <span className="absolute top-2 left-2 z-10 text-[10px] font-bold uppercase tracking-wide bg-white/90 text-ink-strong rounded-full px-2 py-0.5">
                              {t('dashboard.info.cover_badge')}
                            </span>
                          )}
                          <div className="absolute inset-0 z-10 bg-ink-strong/0 group-hover:bg-ink-strong/45 flex flex-col items-center justify-center gap-2 opacity-0 group-hover:opacity-100 transition-all">
                            {!isCover && (
                              <button
                                type="button"
                                onClick={() => setAsCover(item.key)}
                                className="px-2.5 py-1 rounded-full bg-white text-ink-strong text-[11px] font-bold hover:bg-brand-softer"
                              >
                                {t('dashboard.info.set_as_cover')}
                              </button>
                            )}
                            <button
                              type="button"
                              onClick={() => removeMedia(item.key)}
                              aria-label={t('dashboard.edit.remove')}
                              className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-white text-rose-600"
                            >
                              <TrashIcon className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                    {mediaForm.items.length < MAX_MEDIA && (
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        className="aspect-square rounded-xl border-2 border-dashed border-line hover:border-yellow-03 hover:bg-brand-softer text-muted hover:text-yellow-03 flex flex-col items-center justify-center gap-1 transition-colors"
                      >
                        <PlusIcon className="w-5 h-5" />
                        <span className="text-[11px] font-bold">
                          {t('dashboard.info.add_image')}
                        </span>
                      </button>
                    )}
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="w-full flex flex-col items-center justify-center gap-2 py-10 rounded-xl border-2 border-dashed border-line text-muted hover:text-yellow-03 hover:border-yellow-03 hover:bg-brand-softer transition-colors"
                  >
                    <ArrowUpTrayIcon className="w-7 h-7" />
                    <span className="text-[12.5px] font-bold">
                      {t('dashboard.info.upload')}
                    </span>
                  </button>
                )}
                <EditActions
                  onCancel={cancelMedia}
                  onSave={saveMedia}
                  saving={saving}
                />
              </>
            ) : media.length > 0 ? (
              <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-5 gap-3">
                {media.slice(0, 9).map((url, i) => (
                  <div
                    key={`${url}-${i}`}
                    className="relative aspect-square rounded-xl overflow-hidden bg-paper-2"
                    style={{
                      backgroundColor:
                        GALLERY_TINTS[i % GALLERY_TINTS.length],
                    }}
                  >
                    <img
                      src={url}
                      alt=""
                      className="absolute inset-0 w-full h-full object-cover"
                      loading="lazy"
                    />
                    {i === 0 && (
                      <span className="absolute top-2 left-2 z-10 text-[10px] font-bold uppercase tracking-wide bg-white/90 text-ink-strong rounded-full px-2 py-0.5">
                        {t('dashboard.info.cover_badge')}
                      </span>
                    )}
                  </div>
                ))}
                {media.length < MAX_MEDIA && canStartEditing && (
                  <button
                    type="button"
                    onClick={startEditMedia}
                    className="aspect-square rounded-xl border-2 border-dashed border-line hover:border-yellow-03 hover:bg-brand-softer text-muted hover:text-yellow-03 flex flex-col items-center justify-center gap-1 transition-colors"
                  >
                    <PlusIcon className="w-5 h-5" />
                    <span className="text-[11px] font-bold">
                      {t('dashboard.info.add_image')}
                    </span>
                  </button>
                )}
              </div>
            ) : (
              <button
                type="button"
                onClick={startEditMedia}
                className="w-full flex flex-col items-center justify-center gap-2 py-10 rounded-xl border-2 border-dashed border-line text-muted hover:text-yellow-03 hover:border-yellow-03 hover:bg-brand-softer transition-colors"
              >
                <PhotoIcon className="w-7 h-7" />
                <span className="text-[12.5px] font-bold">
                  {t('dashboard.info.no_gallery')}
                </span>
              </button>
            )}
          </Card>

          {/* Description — inline edit */}
          <Card>
            <CardHeader
              eyebrow={t('dashboard.info.about_eyebrow')}
              title={t('dashboard.info.description_title')}
              action={
                isEditing('description') ? null : (
                  canStartEditing && (
                    <PencilBtn
                      onClick={startEditDescription}
                      label={t('dashboard.edit_profile')}
                    />
                  )
                )
              }
            />
            {isEditing('description') ? (
              <div className="space-y-5">
                <div>
                  <FieldLabel>{t('dashboard.short_description')}</FieldLabel>
                  <Textarea
                    value={descForm.description}
                    onChange={(e) =>
                      setDescForm((prev) => ({
                        ...prev,
                        description: e.target.value.slice(0, 200),
                      }))
                    }
                    maxLength={200}
                  />
                  <div className="text-[11px] text-muted mt-1">
                    {descForm.description.length}/200
                  </div>
                </div>

                <div>
                  <FieldLabel>{t('dashboard.about_your_brand')}</FieldLabel>
                  <Textarea
                    value={descForm.longDescription}
                    onChange={(e) =>
                      setDescForm((prev) => ({
                        ...prev,
                        longDescription: e.target.value.slice(0, 10000),
                      }))
                    }
                    maxLength={10000}
                  />
                  <div className="text-[11px] text-muted mt-1">
                    {descForm.longDescription.length}/10000
                  </div>
                </div>

                <div>
                  <FieldLabel>{t('dashboard.info.keywords_label')}</FieldLabel>
                  <div className="flex flex-wrap gap-2 mb-2">
                    {descForm.keywords.map((kw, i) => (
                      <span
                        key={`${kw}-${i}`}
                        className="inline-flex items-center gap-1.5 bg-brand-softer text-ink-strong px-2.5 py-1 rounded-full text-[12.5px] font-medium"
                      >
                        {kw}
                        <button
                          type="button"
                          onClick={() => removeKeyword(i)}
                          aria-label={t('dashboard.edit.remove_keyword')}
                          className="text-muted hover:text-rose-600"
                        >
                          <XMarkIcon className="w-3.5 h-3.5" />
                        </button>
                      </span>
                    ))}
                  </div>
                  <div className="flex items-center gap-2">
                    <TextInput
                      value={descForm.keywordDraft}
                      onChange={(e) =>
                        setDescForm((prev) => ({
                          ...prev,
                          keywordDraft: e.target.value,
                        }))
                      }
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          addKeyword();
                        }
                      }}
                      placeholder={t('dashboard.edit.new_keyword_placeholder')}
                    />
                    <button
                      type="button"
                      onClick={addKeyword}
                      className="inline-flex items-center justify-center h-11 px-4 rounded-xl bg-ink-strong text-white text-[13px] font-bold shrink-0"
                    >
                      {t('dashboard.edit.add_keyword')}
                    </button>
                  </div>
                </div>

                <EditActions
                  onCancel={closeEditor}
                  onSave={saveDescription}
                  saving={saving}
                />
              </div>
            ) : (
              <>
                {!brand.description && !brand.longDescription ? (
                  <p className="text-[14.5px] leading-relaxed text-ink whitespace-pre-wrap">
                    {t('dashboard.no_description')}
                  </p>
                ) : (
                  <>
                    {brand.description && (
                      <div>
                        <div className="text-[11px] uppercase tracking-[0.08em] text-yellow-03 font-bold mb-3">
                          {t('dashboard.short_description')}
                        </div>
                        <p className="text-[14.5px] leading-relaxed text-ink whitespace-pre-wrap">
                          {brand.description}
                        </p>
                      </div>
                    )}
                    {brand.longDescription && (
                      <div
                        className={
                          brand.description
                            ? 'mt-5 pt-5 border-t border-line'
                            : undefined
                        }
                      >
                        <div className="text-[11px] uppercase tracking-[0.08em] text-yellow-03 font-bold mb-3">
                          {t('dashboard.about_your_brand')}
                        </div>
                        <p className="text-[14.5px] leading-relaxed text-ink whitespace-pre-wrap">
                          {brand.longDescription}
                        </p>
                      </div>
                    )}
                  </>
                )}
                {keywords.length > 0 && (
                  <div className="mt-5 pt-5 border-t border-line">
                    <div className="text-[11px] uppercase tracking-[0.08em] text-yellow-03 font-bold mb-3">
                      {t('dashboard.info.keywords_label')}
                    </div>
                    <p className="text-[13.5px] text-ink/70 break-words">
                      {keywords
                        .map((k) =>
                          typeof k === 'string'
                            ? k.charAt(0).toUpperCase() + k.slice(1)
                            : k
                        )
                        .join(' · ')}
                    </p>
                  </div>
                )}
                {brand.history && brand.history.trim() !== '' && (
                  <div className="mt-5 pt-5 border-t border-line">
                    <div className="text-[11px] uppercase tracking-[0.08em] text-yellow-03 font-bold mb-2">
                      {t('dashboard.info.history_title')}
                    </div>
                    <p className="text-[13.5px] leading-relaxed text-ink whitespace-pre-wrap">
                      {brand.history}
                    </p>
                  </div>
                )}
              </>
            )}
          </Card>

          {/* Categories — inline edit */}
          <Card>
            <CardHeader
              eyebrow={t('dashboard.info.classification_eyebrow')}
              title={t('dashboard.info.categories_title')}
              sub={t('dashboard.info.categories_sub')}
              action={
                isEditing('categories') ? null : (
                  canStartEditing && (
                    <PencilBtn
                      onClick={startEditCategories}
                      label={t('dashboard.edit_profile')}
                    />
                  )
                )
              }
            />
            {isEditing('categories') ? (
              <>
                <CategorySelectorGrid
                  key={`cats-edit-${brand?.id}`}
                  selectedMacros={catsForm.macros}
                  setSelectedMacros={setCatsMacros}
                  selectedMicros={catsForm.micros}
                  setSelectedMicros={setCatsMicros}
                  showMicros
                />
                <EditActions
                  onCancel={closeEditor}
                  onSave={saveCategories}
                  saving={saving}
                />
              </>
            ) : categories.length > 0 ? (
              <CategorySelectorGrid
                key={`cats-${brand?.id}`}
                initialCategories={categories}
                restrictToSelected
                readOnly
                showMicros
                noMicroTitle={false}
              />
            ) : (
              <p className="text-[13.5px] text-muted italic">
                {t('dashboard.info.no_categories')}
              </p>
            )}
          </Card>
        </div>

        {/* Right column */}
        <div className="space-y-6 min-w-0">
          {/* Contact — website inline, email locked, location → legacy form */}
          <Card>
            <CardHeader
              eyebrow={t('dashboard.info.contact_eyebrow')}
              title={t('dashboard.info.contact_title')}
              action={
                isEditing('contact') ? null : (
                  canStartEditing && (
                    <PencilBtn
                      onClick={startEditContact}
                      label={t('dashboard.edit_profile')}
                    />
                  )
                )
              }
            />
            {isEditing('contact') ? (
              <div className="space-y-4">
                <div>
                  <FieldLabel>{t('dashboard.info.email_label')}</FieldLabel>
                  <div className="flex items-center gap-2 px-3.5 h-11 rounded-xl bg-paper-2 border border-line text-[14px] text-muted">
                    <LockClosedIcon className="w-4 h-4 shrink-0" />
                    <span className="truncate">{brand.email || '—'}</span>
                  </div>
                  <div className="text-[11px] text-muted mt-1">
                    {t('dashboard.edit.email_locked')}
                  </div>
                </div>
                <div>
                  <FieldLabel>{t('dashboard.info.website_label')}</FieldLabel>
                  <TextInput
                    value={contactForm.website}
                    onChange={(e) =>
                      setContactForm({ website: e.target.value })
                    }
                    placeholder="https://..."
                    inputMode="url"
                  />
                </div>
                <div>
                  <FieldLabel>{t('dashboard.info.location_label')}</FieldLabel>
                  <button
                    type="button"
                    onClick={onEdit}
                    className="w-full flex items-center justify-between gap-2 px-3.5 h-11 rounded-xl bg-white border border-line hover:border-ink/30 text-[14px] text-ink-strong"
                  >
                    <span className="truncate">{locationStr || '—'}</span>
                    <span className="text-[11px] font-bold text-yellow-03 shrink-0">
                      {t('dashboard.edit.location_open_full_editor')} →
                    </span>
                  </button>
                </div>
                <EditActions
                  onCancel={closeEditor}
                  onSave={saveContact}
                  saving={saving}
                />
              </div>
            ) : (
              <div className="space-y-3">
                <ContactView
                  icon={EnvelopeIcon}
                  label={t('dashboard.info.email_label')}
                  value={brand.email}
                  href={brand.email ? `mailto:${brand.email}` : null}
                  locked
                />
                <ContactView
                  icon={GlobeAltIcon}
                  label={t('dashboard.info.website_label')}
                  value={brand.website}
                  href={websiteHref}
                />
                <ContactView
                  icon={MapPinIcon}
                  label={t('dashboard.info.location_label')}
                  value={locationStr}
                />
              </div>
            )}
          </Card>

          {/* Social — inline edit */}
          <Card>
            <CardHeader
              eyebrow={t('dashboard.info.social_eyebrow')}
              title={t('dashboard.info.social_title')}
              action={
                isEditing('social') ? null : (
                  canStartEditing && (
                    <PencilBtn
                      onClick={startEditSocial}
                      label={t('dashboard.edit_profile')}
                    />
                  )
                )
              }
            />
            {isEditing('social') ? (
              <div className="space-y-4">
                {['facebook', 'instagram', 'tiktok'].map((key) => (
                  <div key={key}>
                    <FieldLabel>{SOCIAL_LABELS[key]}</FieldLabel>
                    <TextInput
                      value={socialForm[key]}
                      onChange={(e) =>
                        setSocialForm((prev) => ({
                          ...prev,
                          [key]: e.target.value,
                        }))
                      }
                      placeholder="https://..."
                      inputMode="url"
                    />
                  </div>
                ))}

                <div className="pt-4 border-t border-line">
                  <div className="flex items-center justify-between mb-3">
                    <FieldLabel>
                      {t('dashboard.info.custom_links_title')}
                    </FieldLabel>
                    <button
                      type="button"
                      onClick={addCustomSocial}
                      className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full text-ink hover:bg-paper-2 text-[12.5px] font-bold"
                    >
                      <PlusIcon className="w-4 h-4" />
                      {t('dashboard.info.add_custom_link')}
                    </button>
                  </div>
                  {socialForm.others.length > 0 && (
                    <div className="space-y-2">
                      {socialForm.others.map((row) => (
                        <div
                          key={row.id}
                          className="flex items-start gap-2"
                        >
                          <div className="flex-1 grid grid-cols-1 sm:grid-cols-[1fr_2fr] gap-2 min-w-0">
                            <TextInput
                              value={row.label}
                              onChange={(e) =>
                                updateCustomSocial(
                                  row.id,
                                  'label',
                                  e.target.value
                                )
                              }
                              placeholder={t(
                                'dashboard.info.link_label_placeholder'
                              )}
                            />
                            <TextInput
                              value={row.url}
                              onChange={(e) =>
                                updateCustomSocial(row.id, 'url', e.target.value)
                              }
                              placeholder="https://..."
                              inputMode="url"
                            />
                          </div>
                          <button
                            type="button"
                            onClick={() => removeCustomSocial(row.id)}
                            aria-label={t('dashboard.edit.remove')}
                            className="w-11 h-11 inline-flex items-center justify-center rounded-xl text-muted hover:text-rose-600 hover:bg-paper-2 shrink-0"
                          >
                            <XMarkIcon className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <EditActions
                  onCancel={closeEditor}
                  onSave={saveSocial}
                  saving={saving}
                />
              </div>
            ) : socialList.length > 0 ? (
              <div className="space-y-2.5">
                {socialList.map((s, idx) => {
                  const iconSrc = SOCIAL_ICONS[s.key];
                  return (
                    <a
                      key={`${s.key}-${idx}`}
                      href={s.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-between gap-3 p-2.5 -mx-1 rounded-xl hover:bg-paper-2 transition-colors"
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-9 h-9 rounded-lg bg-brand-softer flex items-center justify-center shrink-0">
                          {iconSrc ? (
                            <img src={iconSrc} alt="" className="w-4 h-4" />
                          ) : (
                            <LinkIcon className="w-4 h-4 text-yellow-03" />
                          )}
                        </div>
                        <div className="min-w-0">
                          <div className="text-[13.5px] font-bold text-ink-strong">
                            {s.label}
                          </div>
                          <div
                            className="text-[12px] text-muted truncate"
                            title={s.url}
                          >
                            {s.url}
                          </div>
                        </div>
                      </div>
                      <ArrowRightIcon className="w-4 h-4 text-muted shrink-0" />
                    </a>
                  );
                })}
              </div>
            ) : (
              <p className="text-[13.5px] text-muted italic">
                {t('dashboard.info.no_social')}
              </p>
            )}
          </Card>
        </div>
      </div>

      {isCropping && imagePreview && (
        <ImageCropper
          imageSrc={imagePreview}
          aspect={1}
          shape="round"
          onCropSave={async (output) => {
            const blob = await handleCropSave(output);
            if (blob) await submitAvatar(blob);
          }}
          onClose={() => {
            setIsCropping(false);
            resetImage();
          }}
        />
      )}
    </div>
  );
};

export default BrandInfo;
